import { BehaviorSubject, takeWhile, lastValueFrom } from 'rxjs';
import { ChangeRecord } from './indexed-storage';

type StorageEmitterChangeRecord<T> = Pick<ChangeRecord<T>, 'value' | 'keys'>;

export class StorageEmitter<T extends { id: string }> {
    private readonly listeners = new Map<string, Set<(...args: unknown[]) => unknown>>();
    private map: { [id: string]: T } = {};
    private readonly loadedSubject$ = new BehaviorSubject(false);
    public readonly loaded$ = this.loadedSubject$.asObservable();

    wait(): Promise<boolean | undefined> {
        return lastValueFrom(
            this.loaded$.pipe(takeWhile(x => !x)),
            { defaultValue: undefined },
        );
    }

    on(event: 'load', fn: (arg: T[]) => unknown): this;
    on(event: 'update', fn: (arg: StorageEmitterChangeRecord<T>) => unknown): this;
    on(event: 'delete', fn: (arg: T) => unknown): this;
    on(event: 'request-more', fn: () => unknown): this;
    on(event: 'more-loaded', fn: (arg: T[]) => unknown): this;
    on(event: 'no-more', fn: () => unknown): this;
    on(event: 'close', fn: () => unknown): this;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    on(event: string, fn: (...args: any[]) => unknown): this {
        if (!this.listeners.has(event)) this.listeners.set(event, new Set());
        this.listeners.get(event)?.add(fn);
        if (event === 'load' && this.loadedSubject$.value) this.emit('load', Object.values(this.map));
        return this;
    }

    off(event: string, fn: (...args: unknown[]) => unknown): this {
        this.listeners.get(event)?.delete(fn);
        return this;
    }

    emit(event: string, ...args: unknown[]) {
        this.listeners.get(event)?.forEach(x => x(...args));
    }

    loading() {
        this.loadedSubject$.next(false);
    }

    load(records: T[], more = false) {
        if (!more) this.map = {};
        records.forEach(x => (this.map[x.id] = x));
        this.loadedSubject$.next(true);
        this.emit(more ? 'more-loaded' : 'load', records);
    }

    next(value: StorageEmitterChangeRecord<T>) {
        this.map[value.value.id] = value.value;
        this.emit('update', value);
    }

    deleted(id: string) {
        const existing = this.map[id];
        if (existing) {
            delete this.map[id];
            this.emit('delete', existing);
        }
    }

    more() {
        return this.emit('request-more');
    }

    close() {
        this.emit('close');
        this.listeners.clear();
    }
}
